import { ChatAddress, ChatAddressKind } from './types'

/** Sekmelerin sabit sırası: fatura önce (en sık sorulan "fatura adresim ne"). */
const KIND_ORDER: ChatAddressKind[] = ['invoice', 'shipping']

const KIND_TAB_LABEL: Record<ChatAddressKind, string> = {
  invoice: 'Fatura',
  shipping: 'Sevk',
}

/** Sayaç satırında kullanılan tür adı ("1 fatura adresi"). */
const KIND_COUNT_LABEL: Record<ChatAddressKind, string> = {
  invoice: 'fatura',
  shipping: 'sevk',
}

/** Tek sekme: tür + o türe düşen adresler. */
export interface AddressTab {
  kind: ChatAddressKind
  /** "Fatura (1)" */
  label: string
  addresses: ChatAddress[]
}

/** Kartın render etmesi gereken her şeyi taşıyan, tek karar noktasına indirgenmiş görünüm. */
export interface AddressView {
  tabs: AddressTab[]
  /** Seçili sekme; hiç adres yoksa `undefined` (kart bu durumda hiç render edilmez). */
  activeTab: AddressTab | undefined
  /** Sayaç satırı metni ("4 sevk adresi"). */
  countLabel: string
}

/** Adresleri türe göre sekmelere böler. Boş tür için sekme üretilmez — bilgi taşımaz. */
export function buildTabs(addresses: ChatAddress[]): AddressTab[] {
  return KIND_ORDER.map(kind => {
    const rows = addresses.filter(item => item.kind === kind)
    return { kind, label: `${KIND_TAB_LABEL[kind]} (${rows.length})`, addresses: rows }
  }).filter(tab => tab.addresses.length > 0)
}

/** "KONUR BİLİŞİM S — ÇANKAYA / Ankara": başlık varsa yer bilgisinin önüne alınır. */
export function addressHeading(address: ChatAddress): string {
  const place = [address.district, address.city].filter(Boolean).join(' / ')
  if (!address.title) return place
  return place ? `${address.title} — ${place}` : address.title
}

/**
 * Adres gövdesi. Mahalle Türkçe adres sırasına göre sokağın önüne alınır; ancak legacy veride
 * mahalle bilgisi bazen adres metninin İÇİNDE de duruyor — o durumda tekrar edilmez.
 */
export function addressBody(address: ChatAddress): string {
  const line = address.addressLine
  const neighbourhood = address.neighbourhood
  if (!neighbourhood) return line
  if (line.toLocaleLowerCase('tr-TR').includes(neighbourhood.toLocaleLowerCase('tr-TR'))) return line
  return [neighbourhood, line].filter(Boolean).join(', ')
}

/** Üçüncü satır: posta kodu + sevk kodu. Boş dönerse satır hiç çizilmez. */
export function addressMeta(address: ChatAddress): string {
  return [address.postCode, address.sevkCode ? `Sevk kodu ${address.sevkCode}` : null].filter(Boolean).join(' · ')
}

/** Kopyalanacak tam adres metni (sipariş ekranına yapıştırmak için). */
export function addressCopyText(address: ChatAddress): string {
  return [
    address.title,
    address.contactName,
    address.addressLine,
    address.neighbourhood,
    [address.district, address.city].filter(Boolean).join(' / ') || null,
    address.postCode,
  ]
    .filter(Boolean)
    .join('\n')
}

/**
 * `AddressCards`'ın tüm türetilmiş durumunu hesaplar: sekmeler, aktif sekme ve sayaç metni.
 * Kartın tek "sıradan olmayan" mantığı burada toplanır ki tablo testleriyle doğrulanabilsin
 * (bkz. addressesContract.test.ts).
 */
export function resolveAddressView(addresses: ChatAddress[], tabIndex: number): AddressView {
  const tabs = buildTabs(addresses)
  // Veri değişince seçili indeks kapsam dışı kalabilir → ilk sekmeye düşülür.
  const activeTab = tabs[tabIndex] ?? tabs[0]
  const countLabel = activeTab ? `${activeTab.addresses.length} ${KIND_COUNT_LABEL[activeTab.kind]} adresi` : ''

  return { tabs, activeTab, countLabel }
}
